import { FC, useEffect, useState, useCallback } from 'react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { CollectionsTable } from '@/pages/data-sources/collections-table';
import { NewCollectionModal } from '@/pages/data-sources/new-collection-modal';
import { DataSourcesSkeleton } from '@/pages/data-sources/data-sources-skeleton';
import { PageTitle } from '@/components/page-title';
import { getCollections } from '@/services/data-sources/get-collections';
import { Collection } from '@/types/source-types';
import { useNotification } from '@/components/notifications';
import { ROUTES } from '@/constants/routes';

export const DataSourcesPage: FC = () => {
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewCollectionModalOpen, setIsNewCollectionModalOpen] =
    useState(false);

  const fetchCollections = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getCollections();

      if (response && 'detail' in response) {
        notification.error(response.detail);
        return;
      }

      setCollections((response as Collection[]) || []);
    } catch (err) {
      notification.error('Failed to fetch collections');
      console.error('Error fetching collections:', err);
    } finally {
      setIsLoading(false);
    }
  }, [notification]);

  const handleCollectionSoftDeleted = (collectionId: string) => {
    // Mark collection as pending deletion for soft delete
    setCollections((prevCollections) =>
      prevCollections.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              status: 'pending_deletion',
              deletion_requested_at: new Date().toISOString(),
            }
          : collection,
      ),
    );
  };

  const handleCollectionHardDeleted = (collectionId: string) => {
    // Remove the collection entirely for hard delete
    setCollections((prevCollections) =>
      prevCollections.filter((collection) => collection.id !== collectionId),
    );
  };

  const handleCollectionCancelDeletion = (collectionId: string) => {
    setCollections((prevCollections) =>
      prevCollections.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              status: 'active',
              deletion_requested_at: '',
            }
          : collection,
      ),
    );
  };

  const handleCollectionUpdated = (updated: Collection | null) => {
    if (!updated) return;
    setCollections((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
  };

  const navigateToCollection = (collectionId: string) => {
    navigate(`${ROUTES.DATA_SOURCES.ROOT}/${collectionId}`);
  };

  const handleNewCollectionModalOpen = () => {
    setIsNewCollectionModalOpen(true);
  };

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  // Show skeleton while loading
  if (isLoading) {
    return (
      <>
        <DataSourcesSkeleton />
        <NewCollectionModal
          isModalOpen={isNewCollectionModalOpen}
          setIsModalOpen={setIsNewCollectionModalOpen}
          collections={collections}
          setCollections={setCollections}
          navigateToCollection={navigateToCollection}
        />
      </>
    );
  }

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <PageTitle title='Data Collections' />
      </div>
      <CollectionsTable
        collections={collections}
        isLoading={isLoading}
        onCollectionSoftDeleted={handleCollectionSoftDeleted}
        onCollectionHardDeleted={handleCollectionHardDeleted}
        handleNewCollectionModalOpen={handleNewCollectionModalOpen}
        onCollectionCancelDeletion={handleCollectionCancelDeletion}
        onCollectionUpdated={handleCollectionUpdated}
      />
      <NewCollectionModal
        isModalOpen={isNewCollectionModalOpen}
        setIsModalOpen={setIsNewCollectionModalOpen}
        collections={collections}
        setCollections={setCollections}
        navigateToCollection={navigateToCollection}
      />
    </div>
  );
};
